import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { PURGE } from "redux-persist"
import { Location } from "./locationsSlice"

// A trailer (or any roomless asset) tracked by GPS. `id` is the pubsub item
// id ("{gps_config_id}:{device_id}"), `label` is what the provider calls it,
// usually the plates. There is no chat, driver, route or tacho behind it.
export type TrailerLocation = {
  id: string
  label?: string
  location: Location | undefined
  receivedAt?: number
}

export type TrailerLocations = Record<string, TrailerLocation>

const initialState: TrailerLocations = {}

type UpsertTrailerLocationAction = { trailerLocation: TrailerLocation }
type BatchUpsertTrailerLocationsAction = { trailerLocations: TrailerLocation[] }
type RemoveTrailerLocationAction = { id: string }

const trailerLocations = createSlice({
  name: "trailerLocations",
  initialState,
  reducers: {
    upsertTrailerLocation: (
      state,
      action: PayloadAction<UpsertTrailerLocationAction>,
    ) => {
      const { trailerLocation } = action.payload
      state[trailerLocation.id] = { ...trailerLocation, receivedAt: Date.now() }
    },
    batchUpsertTrailerLocations: (
      state,
      action: PayloadAction<BatchUpsertTrailerLocationsAction>,
    ) => {
      const now = Date.now()
      for (const trailerLocation of action.payload.trailerLocations) {
        state[trailerLocation.id] = { ...trailerLocation, receivedAt: now }
      }
    },
    removeTrailerLocation: (
      state,
      action: PayloadAction<RemoveTrailerLocationAction>,
    ) => {
      delete state[action.payload.id]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const {
  upsertTrailerLocation,
  batchUpsertTrailerLocations,
  removeTrailerLocation,
} = trailerLocations.actions

export const selectTrailerLocations = (state: {
  trailerLocations: TrailerLocations
}) => state.trailerLocations

export default trailerLocations.reducer
